/**
 * Draft location tags held in client state before first request save (#999).
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

export interface LocationTagDraft {
  clientId: number;
  name: string;
}

interface LocationTagsDraftContextValue {
  draftTags: LocationTagDraft[];
  addDraftTag: (name: string) => number;
  renameDraftTag: (clientId: number, name: string) => void;
  deleteDraftTag: (clientId: number) => void;
  clearDraftTags: () => void;
  /** #999 — restore draft tags from a persisted create-request snapshot after refresh. */
  hydrateDraftTags: (tags: LocationTagDraft[]) => void;
}

const LocationTagsDraftContext =
  createContext<LocationTagsDraftContextValue | null>(null);

function normalizeDraftName(raw: string): string {
  return raw
    .replace(/[\x00-\x1F\x7F]/g, '')
    .replace(/[<>]/g, '')
    .trim();
}

export function LocationTagsDraftProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [draftTags, setDraftTags] = useState<LocationTagDraft[]>([]);
  const nextClientIdRef = useRef(-1);

  const addDraftTag = useCallback((rawName: string): number => {
    const name = normalizeDraftName(rawName);
    if (!name) {
      throw new Error('Location tag name cannot be empty');
    }

    let createdClientId = 0;

    setDraftTags(prev => {
      const duplicate = prev.some(
        tag => tag.name.toLowerCase() === name.toLowerCase(),
      );
      if (duplicate) {
        throw new Error(
          `Location tag name "${name}" is already used in this request. Tag names must be unique.`,
        );
      }

      createdClientId = nextClientIdRef.current;
      nextClientIdRef.current -= 1;
      return [...prev, { clientId: createdClientId, name }];
    });

    return createdClientId;
  }, []);

  const renameDraftTag = useCallback((clientId: number, rawName: string) => {
    const name = normalizeDraftName(rawName);
    if (!name) {
      throw new Error('Location tag name cannot be empty');
    }

    setDraftTags(prev => {
      const duplicate = prev.some(
        tag =>
          tag.clientId !== clientId &&
          tag.name.toLowerCase() === name.toLowerCase(),
      );
      if (duplicate) {
        throw new Error(
          `Location tag name "${name}" is already used in this request. Tag names must be unique.`,
        );
      }

      return prev.map(tag =>
        tag.clientId === clientId ? { ...tag, name } : tag,
      );
    });
  }, []);

  const deleteDraftTag = useCallback((clientId: number) => {
    setDraftTags(prev => prev.filter(tag => tag.clientId !== clientId));
  }, []);

  const clearDraftTags = useCallback(() => {
    setDraftTags([]);
    nextClientIdRef.current = -1;
  }, []);

  const hydrateDraftTags = useCallback((tags: LocationTagDraft[]) => {
    if (!Array.isArray(tags) || tags.length === 0) {
      return;
    }
    setDraftTags(prev => {
      // Only hydrate into an untouched context so we never clobber in-session edits.
      if (prev.length > 0) {
        return prev;
      }
      const restored = tags.map(tag => ({
        clientId: tag.clientId,
        name: tag.name,
      }));
      const minClientId = Math.min(...restored.map(tag => tag.clientId));
      nextClientIdRef.current = Math.min(-1, minClientId - 1);
      return restored;
    });
  }, []);

  const value = useMemo(
    () => ({
      draftTags,
      addDraftTag,
      renameDraftTag,
      deleteDraftTag,
      clearDraftTags,
      hydrateDraftTags,
    }),
    [
      addDraftTag,
      clearDraftTags,
      deleteDraftTag,
      draftTags,
      hydrateDraftTags,
      renameDraftTag,
    ],
  );

  return (
    <LocationTagsDraftContext.Provider value={value}>
      {children}
    </LocationTagsDraftContext.Provider>
  );
}

export function useLocationTagsDraft(): LocationTagsDraftContextValue {
  const context = useContext(LocationTagsDraftContext);
  if (!context) {
    throw new Error(
      'useLocationTagsDraft must be used within LocationTagsDraftProvider',
    );
  }
  return context;
}

export function useOptionalLocationTagsDraft(): LocationTagsDraftContextValue | null {
  return useContext(LocationTagsDraftContext);
}
